import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import {
  FiAlignCenter,
  FiAlignLeft,
  FiAlignRight,
  FiBold,
  FiCode,
  FiCornerUpLeft,
  FiCornerUpRight,
  FiEdit3,
  FiImage,
  FiItalic,
  FiLink,
  FiList,
  FiMinus,
  FiSlash,
  FiType,
  FiTrash2,
  FiUnderline,
  FiUploadCloud,
  FiVideo,
  FiX,
} from 'react-icons/fi';
import ArticleVideo from '../extensions/ArticleVideo';
import { getArticleVideoData } from '../utils/articleVideo';
import './ProductRichTextEditor.css';

const ToolbarButton = ({ label, active = false, disabled = false, onClick, children }) => (
  <button
    type="button"
    className={active ? 'is-active' : ''}
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
  >
    {children}
  </button>
);

const normalizeUrl = (value, { allowRelative = false } = {}) => {
  const url = String(value || '').trim();
  if (!url) return '';
  if (allowRelative && url.startsWith('/')) return url;
  if (/^(https?:|mailto:|tel:|#)/i.test(url)) return url;
  if (/^[a-z][a-z\d+.-]*:/i.test(url)) throw new Error('این نوع آدرس مجاز نیست.');
  return `https://${url}`;
};

const ProductRichTextEditor = ({
  value,
  onChange,
  onUploadImage,
  placeholder = 'توضیحات کامل محصول را اینجا بنویسید...',
  ariaLabel = 'توضیحات محصول',
  editorId = 'product-description',
  imageAlt = 'تصویر محصول',
  enableVideo = false,
  onUploadVideo,
  direction = 'rtl',
  videoContentTitle = 'ویدیوی محتوا',
  videoDialogTitle = 'افزودن ویدیو به محتوا',
}) => {
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [editorError, setEditorError] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
        link: {
          openOnClick: false,
          autolink: true,
          defaultProtocol: 'https',
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer nofollow',
          },
        },
      }),
      Image.configure({
        allowBase64: false,
        HTMLAttributes: { loading: 'lazy' },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
      ...(enableVideo ? [ArticleVideo] : []),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        id: editorId,
        class: 'admin-rich-text-content',
        dir: direction,
        spellcheck: 'true',
        'aria-label': ariaLabel,
      },
    },
    onUpdate: ({ editor: currentEditor }) => onChange(currentEditor.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    const nextValue = value || '';
    if (editor.getHTML() !== nextValue) {
      editor.commands.setContent(nextValue, { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    if (!editor) return;
    editor.setOptions({
      editorProps: {
        attributes: {
          id: editorId,
          class: 'admin-rich-text-content',
          dir: direction,
          spellcheck: 'true',
          'aria-label': ariaLabel,
        },
      },
    });
  }, [ariaLabel, direction, editor, editorId]);

  useEffect(() => {
    if (!videoDialogOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape' && !videoUploading) setVideoDialogOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [videoDialogOpen, videoUploading]);

  if (!editor) return null;

  const setLink = () => {
    setEditorError('');
    const previousUrl = editor.getAttributes('link').href || '';
    const enteredUrl = window.prompt('آدرس لینک را وارد کنید:', previousUrl);
    if (enteredUrl === null) return;

    try {
      const href = normalizeUrl(enteredUrl, { allowRelative: true });
      if (!href) {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    } catch (error) {
      setEditorError(error.message);
    }
  };

  const addImageByUrl = () => {
    setEditorError('');
    const enteredUrl = window.prompt('آدرس اینترنتی تصویر را وارد کنید:');
    if (!enteredUrl) return;

    try {
      const src = normalizeUrl(enteredUrl, { allowRelative: true });
      const alt = window.prompt('متن جایگزین تصویر را بنویسید:', imageAlt) || imageAlt;
      editor.chain().focus().setImage({ src, alt }).run();
    } catch (error) {
      setEditorError(error.message);
    }
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !onUploadImage) return;

    setImageUploading(true);
    setEditorError('');
    try {
      const src = await onUploadImage(file);
      if (!src) throw new Error('آدرس تصویر از سرور دریافت نشد.');
      const alt = window.prompt('متن جایگزین تصویر را برای دسترس‌پذیری و سئو بنویسید:', imageAlt);
      editor.chain().focus().setImage({ src, alt: alt === null ? imageAlt : alt.trim() }).run();
    } catch (error) {
      setEditorError(error.message || 'آپلود تصویر انجام نشد.');
    } finally {
      setImageUploading(false);
    }
  };

  const editSelectedImageAlt = () => {
    if (!editor.isActive('image')) return;
    const currentAlt = editor.getAttributes('image').alt || '';
    const alt = window.prompt('متن جایگزین تصویر:', currentAlt || imageAlt);
    if (alt === null) return;
    editor.chain().focus().updateAttributes('image', { alt: alt.trim() }).run();
  };

  const insertVideo = ({ src, provider, title }) => {
    if (!src || !provider) throw new Error('اطلاعات ویدیو کامل نیست.');
    const inserted = editor.chain().focus().insertContent({
      type: 'articleVideo',
      attrs: { src, provider, title: title || videoContentTitle },
    }).run();
    if (!inserted) throw new Error('افزودن ویدیو به متن انجام نشد.');
  };

  const addVideoByUrl = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!videoUrl.trim() || videoUploading) return;

    setEditorError('');
    try {
      insertVideo(getArticleVideoData(videoUrl));
      setVideoUrl('');
      setVideoDialogOpen(false);
    } catch (error) {
      setEditorError(error.message || 'لینک ویدیو معتبر نیست.');
    }
  };

  const uploadVideo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !onUploadVideo) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!['mp4', 'webm'].includes(extension) || !['video/mp4', 'video/webm'].includes(file.type)) {
      setEditorError('فقط فایل MP4 یا WebM قابل آپلود است.');
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      setEditorError('حجم ویدیو نباید بیشتر از ۲۰۰ مگابایت باشد.');
      return;
    }

    setVideoUploading(true);
    setEditorError('');
    try {
      const src = await onUploadVideo(file);
      if (!src) throw new Error('آدرس ویدیو از سرور دریافت نشد.');
      insertVideo({ src, provider: 'file', title: file.name || videoContentTitle });
      setVideoDialogOpen(false);
    } catch (error) {
      setEditorError(error.message || 'آپلود ویدیو انجام نشد.');
    } finally {
      setVideoUploading(false);
    }
  };

  return (
    <div className="admin-rich-text-editor">
      <div className="admin-rich-text-toolbar" role="toolbar" aria-label="ابزارهای ویرایش متن">
        <div className="admin-rich-text-tool-group">
          <ToolbarButton label="متن معمولی" active={editor.isActive('paragraph')} onClick={() => editor.chain().focus().setParagraph().run()}>
            <FiType />
          </ToolbarButton>
          <ToolbarButton label="عنوان سطح دو" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
            <span className="admin-editor-text-icon">H2</span>
          </ToolbarButton>
          <ToolbarButton label="عنوان سطح سه" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
            <span className="admin-editor-text-icon">H3</span>
          </ToolbarButton>
        </div>

        <div className="admin-rich-text-tool-group">
          <ToolbarButton label="پررنگ" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><FiBold /></ToolbarButton>
          <ToolbarButton label="مورب" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><FiItalic /></ToolbarButton>
          <ToolbarButton label="زیرخط" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><FiUnderline /></ToolbarButton>
          <ToolbarButton label="خط‌خورده" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><FiSlash /></ToolbarButton>
          <ToolbarButton label="کد درون‌خطی" active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}><FiCode /></ToolbarButton>
        </div>

        <div className="admin-rich-text-tool-group">
          <ToolbarButton label="فهرست نشانه‌دار" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><FiList /></ToolbarButton>
          <ToolbarButton label="فهرست شماره‌دار" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><span className="admin-editor-text-icon">1.</span></ToolbarButton>
          <ToolbarButton label="نقل‌قول" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}><span className="admin-editor-text-icon">❞</span></ToolbarButton>
          <ToolbarButton label="خط جداکننده" onClick={() => editor.chain().focus().setHorizontalRule().run()}><FiMinus /></ToolbarButton>
        </div>

        <div className="admin-rich-text-tool-group">
          <ToolbarButton label="راست‌چین" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}><FiAlignRight /></ToolbarButton>
          <ToolbarButton label="وسط‌چین" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}><FiAlignCenter /></ToolbarButton>
          <ToolbarButton label="چپ‌چین" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}><FiAlignLeft /></ToolbarButton>
        </div>

        <div className="admin-rich-text-tool-group">
          <ToolbarButton label="افزودن یا ویرایش لینک" active={editor.isActive('link')} onClick={setLink}><FiLink /></ToolbarButton>
          <ToolbarButton label="حذف لینک" disabled={!editor.isActive('link')} onClick={() => editor.chain().focus().unsetLink().run()}><FiSlash /></ToolbarButton>
          <ToolbarButton label="افزودن تصویر با آدرس" onClick={addImageByUrl}><FiImage /></ToolbarButton>
          <ToolbarButton label="آپلود تصویر" disabled={imageUploading || !onUploadImage} onClick={() => imageInputRef.current?.click()}><FiUploadCloud /></ToolbarButton>
          <ToolbarButton label="ویرایش متن جایگزین تصویر" disabled={!editor.isActive('image')} onClick={editSelectedImageAlt}><FiEdit3 /></ToolbarButton>
          <ToolbarButton label="حذف تصویر انتخاب‌شده" disabled={!editor.isActive('image')} onClick={() => editor.chain().focus().deleteSelection().run()}><FiTrash2 /></ToolbarButton>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
            onChange={uploadImage}
            tabIndex="-1"
            hidden
          />
          {enableVideo && (
            <>
              <ToolbarButton
                label="افزودن ویدیو"
                active={editor.isActive('articleVideo')}
                disabled={videoUploading}
                onClick={() => {
                  setEditorError('');
                  setVideoDialogOpen(true);
                }}
              >
                <FiVideo />
              </ToolbarButton>
              <ToolbarButton
                label="حذف ویدیوی انتخاب‌شده"
                disabled={!editor.isActive('articleVideo') || videoUploading}
                onClick={() => editor.chain().focus().deleteSelection().run()}
              >
                <FiTrash2 />
              </ToolbarButton>
            </>
          )}
        </div>

        <div className="admin-rich-text-tool-group admin-rich-text-history">
          <ToolbarButton label="بازگشت" disabled={!editor.can().chain().focus().undo().run()} onClick={() => editor.chain().focus().undo().run()}><FiCornerUpRight /></ToolbarButton>
          <ToolbarButton label="تکرار" disabled={!editor.can().chain().focus().redo().run()} onClick={() => editor.chain().focus().redo().run()}><FiCornerUpLeft /></ToolbarButton>
        </div>
      </div>

      <EditorContent editor={editor} />
      <div className="admin-rich-text-footer">
        <span>
          {videoUploading
            ? 'در حال آپلود و افزودن ویدیو...'
            : imageUploading
              ? 'در حال آپلود و افزودن تصویر...'
              : enableVideo
                ? 'تصویر و ویدیو را می‌توانید در هر بخش از متن قرار دهید.'
                : 'برای ویرایش لینک، متن موردنظر را انتخاب کنید.'}
        </span>
        {editorError && <strong role="alert">{editorError}</strong>}
      </div>

      {videoDialogOpen && createPortal(
        <div
          className="admin-video-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !videoUploading) setVideoDialogOpen(false);
          }}
        >
          <section className="admin-video-dialog" role="dialog" aria-modal="true" aria-labelledby={`${editorId}-video-title`} dir="rtl">
            <header>
              <span className="admin-video-dialog-icon"><FiVideo aria-hidden="true" /></span>
              <div>
                <h3 id={`${editorId}-video-title`}>{videoDialogTitle}</h3>
                <p>از لینک سرویس‌های ویدیویی استفاده کنید یا فایل را مستقیماً آپلود کنید.</p>
              </div>
              <button type="button" className="ui-close-button" onClick={() => setVideoDialogOpen(false)} disabled={videoUploading} aria-label="بستن پنجره">
                <FiX aria-hidden="true" />
              </button>
            </header>

            <div className="admin-video-link-form">
              <label htmlFor={`${editorId}-video-url`}>لینک YouTube، آپارات یا Vimeo</label>
              <div>
                <input
                  id={`${editorId}-video-url`}
                  type="text"
                  inputMode="url"
                  dir="ltr"
                  value={videoUrl}
                  onChange={(event) => setVideoUrl(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                      event.preventDefault();
                      event.stopPropagation();
                      addVideoByUrl(event);
                    }
                  }}
                  placeholder="https://www.youtube.com/watch?v=..."
                  autoFocus
                />
                <button type="button" onClick={addVideoByUrl} disabled={!videoUrl.trim() || videoUploading}>افزودن لینک</button>
              </div>
              <small>لینک معمولی ویدیو به‌صورت خودکار به پخش‌کننده امن و واکنش‌گرا تبدیل می‌شود.</small>
            </div>

            {onUploadVideo && (
              <div className="admin-video-upload-section">
                <div className="admin-video-divider"><span>یا</span></div>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/webm,.mp4,.webm"
                  onChange={uploadVideo}
                  tabIndex="-1"
                  hidden
                />
                <button type="button" onClick={() => videoInputRef.current?.click()} disabled={videoUploading}>
                  <FiUploadCloud aria-hidden="true" />
                  <span>
                    <strong>{videoUploading ? 'در حال آپلود ویدیو...' : 'آپلود مستقیم فایل ویدیو'}</strong>
                    <small>MP4 یا WebM، حداکثر ۲۰۰ مگابایت</small>
                  </span>
                </button>
              </div>
            )}

            {editorError && <p className="admin-video-dialog-error" role="alert">{editorError}</p>}
          </section>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProductRichTextEditor;
